import * as React from 'react';

/**
 * Which fields the operator has touched on this card, plus the wrapper that records
 * it. Validation messages render either way; `isTouched(field)` gates only whether a
 * field's message is a live region, so a card that mounts invalid explains itself
 * without announcing every message at once, and editing one field never turns the
 * blanks the AI left elsewhere into a burst of alerts.
 *
 * Wrap EVERY control's change handler — a Select left unwrapped is a field whose
 * error appears and is announced to nobody. Name every field the edit can make
 * invalid: a requirement Select that materialises a reason field names both, so the
 * error the operator's own action produced is live even though they never typed in it.
 */
export const useInteracted = () => {
  const [touched, setTouched] = React.useState<ReadonlySet<string>>(() => new Set());

  const onEdit = <A extends unknown[]>(
    fields: string | readonly string[],
    handler: (...args: A) => void,
  ) => (...args: A) => {
    setTouched((previous) => {
      const names = typeof fields === 'string' ? [fields] : fields;
      if (names.every((field) => previous.has(field))) return previous;
      const next = new Set(previous);
      names.forEach((field) => next.add(field));
      return next;
    });
    handler(...args);
  };

  const isTouched = (field: string) => touched.has(field);

  return { isTouched, onEdit };
};
